import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchTrendDetail } from '../backend/fetchs';
import { Trend } from '../backend/interfaces';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import IntervalCell from '../components/IntervalCell';

const TrendDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Trend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const result = await fetchTrendDetail(parseInt(id));
        setData(result);
        setError(null);
      } catch (err) {
        setError('Error fetching trend detail');
        console.error('Error fetching trend detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error || 'Trend not found'}</div>
        <Link
          to="/trends"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Trends
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 8,
    }).format(price);
  };

  const formatVolume = (volume: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(volume);
  };

  const getExplanationItems = () => {
    const { price, ema200, ema55, vwap, supertrend } = data.indicators;
    const isLong = data.side.description === 'LONG';
    
    const items = [
      {
        text: `Price is ${isLong ? 'above' : 'below'} Ema 200`,
        check: isLong ? price > ema200 : price < ema200,
      },
      {
        text: `Price is ${isLong ? 'above' : 'below'} Ema 55`,
        check: isLong ? price > ema55 : price < ema55,
      },
      {
        text: `Price is ${isLong ? 'above' : 'below'} VWap`,
        check: isLong ? price > vwap : price < vwap,
      },
      {
        text: `Supertrend is ${data.side.description}`,
        check: (isLong && supertrend === 1) || (!isLong && supertrend === 2),
      },
      {
        text: `Aligned with daily bias which is ${data.daily_bias.side.description}`,
        check: data.side.description === data.daily_bias.side.description,        
      }
    ];

    return items;
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Trend #{data.id}</h1>
        </div>
        
        {/* Main Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-medium text-gray-500">Ticker</h2>
              <p className="text-2xl font-bold text-gray-900">{data.ticker.description}</p>
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Trend Confirmation</h2>
              <DirectionCell direction={data.side.description} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Confirmation Date</h2>
              <DateCell timestamp={data.created_at} blackMode={true} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Interval</h2>
              <IntervalCell interval={data.candlestick.interval.description} />
            </div>
          </div>
        </div>
      </div>

      {/* Indicators Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Indicators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">Price</h3>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(data.indicators.price)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">EMA 200</h3>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(data.indicators.ema200)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">EMA 55</h3>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(data.indicators.ema55)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">VWAP</h3>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(data.indicators.vwap)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">Supertrend</h3>
            <p className="text-lg font-semibold text-gray-900">
              {data.indicators.supertrend === 1 ? 'LONG' : 'SHORT'}
            </p>
          </div>
          {data.daily_bias && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500">Daily Bias</h3>
              <DirectionCell direction={data.daily_bias.side.description} />
            </div>
          )}
        </div>
      </div>

      {/* Explanation */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Explanation</h2>
        <div className="space-y-3">
          {getExplanationItems().map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                item.check ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {item.check ? (
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <span className={`text-md ${item.check ? 'text-green-700' : 'text-red-700'}`}>
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Candlestick Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Candlestick Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open Date</h3>
              <DateCell timestamp={data.candlestick.open_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close Date</h3>
              <DateCell timestamp={data.candlestick.close_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Volume</h3>
              <p className="text-lg font-semibold text-gray-900">{formatVolume(data.candlestick.volume)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Number of Trades</h3>
              <p className="text-lg font-semibold text-gray-900">
                {data.candlestick.number_of_trades.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.open)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.close)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">High</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.high)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Low</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.low)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendDetailPage; 